<template>
    <div class="step5-categories">
        <div v-if="Object.keys(store.validationErrors).length > 0" class="notification is-danger mb-4">
            <ul>
                <li v-for="(errors, field) in store.validationErrors" :key="field">
                    <strong>{{ translateField(String(field)) }}</strong
                    >: {{ Array.isArray(errors) ? errors[0] : errors }}
                </li>
            </ul>
        </div>

        <Step5CategoriesTemplatePicker v-if="view === 'template'" @back="onBackFromSubView" @applied="onTemplateApplied" />

        <Step5CategoriesCopyFromPrevious v-else-if="view === 'copy'" @back="onBackFromSubView" @copied="onCopied" />

        <div v-else-if="view === 'manualExplainer'">
            <a class="is-inline-block mb-3" @click="view = 'picker'">
                <span class="icon"><i class="fas fa-arrow-left"></i></span>
                <span>{{ translate("page.category_manager.back_to_options") }}</span>
            </a>
            <h3 class="has-text-weight-bold mb-2">{{ translate("wizard.step5.manual_explainer_title") }}</h3>
            <p>{{ translate("wizard.step5.manual_explainer_body") }}</p>
        </div>

        <template v-else>
            <div v-if="store.categoriesSetupMethod" class="notification is-success mb-4">
                <span class="icon"><i class="fas fa-check-circle"></i></span>
                <span>{{ stagedSummary }}</span>
                <a class="ml-2" @click="onChange">{{ translate("wizard.step5.change_link") }}</a>
            </div>

            <div v-else class="columns is-centered">
                <div class="column is-6">
                    <div class="box has-no-glow-effect step5-option-card">
                        <h3 class="has-text-weight-bold">📦 {{ translate("wizard.step5.card_template_title") }}</h3>
                        <p class="mb-3">{{ translate("wizard.step5.card_template_description") }}</p>
                        <button class="button is-primary is-fullwidth" data-testid="wizard-card-template" @click="view = 'template'">
                            <span>{{ translate("wizard.step5.select_button") }}</span>
                            <span class="icon"><i class="fas fa-arrow-right"></i></span>
                        </button>
                    </div>
                </div>

                <div class="column is-6" v-if="store.hasPriorEventsForCopy">
                    <div class="box has-no-glow-effect step5-option-card">
                        <h3 class="has-text-weight-bold">📋 {{ translate("wizard.step5.card_copy_title") }}</h3>
                        <p class="mb-3">{{ translate("wizard.step5.card_copy_description") }}</p>
                        <button class="button is-fullwidth" data-testid="wizard-card-copy" @click="view = 'copy'">
                            <span>{{ translate("wizard.step5.select_button") }}</span>
                            <span class="icon"><i class="fas fa-arrow-right"></i></span>
                        </button>
                    </div>
                </div>
            </div>

            <p v-if="!store.categoriesSetupMethod" class="has-text-centered">
                {{ translate("wizard.step5.card_manual_prompt") }}
                <a @click="view = 'manualExplainer'">{{ translate("wizard.step5.card_manual_link") }}</a>
            </p>
        </template>

        <div class="field mt-5" v-if="view === 'picker' || view === 'manualExplainer'">
            <div class="buttons">
                <button class="button" @click="emit('back')">
                    <span class="icon"><i class="fas fa-arrow-left"></i></span>
                    <span>{{ translate("common.back") }}</span>
                </button>
                <LoadingButton
                    :is-loading="store.saving"
                    class="button is-flex-grow-1"
                    :class="store.categoriesSetupMethod ? 'is-primary' : ''"
                    data-testid="wizard-continue"
                    @click="onFinish"
                >
                    <span>{{
                        store.categoriesSetupMethod ? translate("wizard.step5.finish_setup") : translate("wizard.step5.skip_and_finish")
                    }}</span>
                    <span class="icon"><i class="fas fa-arrow-right"></i></span>
                </LoadingButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import LoadingButton from "../../../Common/LoadingButton.vue";
    import { translate } from "../../../Common/translations";
    import { handleError } from "../../../Common/errorHandler";
    import eventsApi, { WizardCategoriesSetupMethod } from "../../../Api/events";
    import { useWizardStore } from "../wizardStore";
    import Step5CategoriesTemplatePicker from "./Step5CategoriesTemplatePicker.vue";
    import Step5CategoriesCopyFromPrevious from "./Step5CategoriesCopyFromPrevious.vue";

    export type StagedCategoriesRecipe = {
        templateNames?: string[];
        categoryPaths?: string[];
        sourceEventId?: number;
    };

    export type CategoriesSetupOutcome = WizardCategoriesSetupMethod | "manual" | "skip";

    type Step5View = "picker" | "template" | "copy" | "manualExplainer";

    const emit = defineEmits<{
        submit: [recipe: StagedCategoriesRecipe | null, method: CategoriesSetupOutcome];
        back: [];
    }>();

    const store = useWizardStore();

    const view = ref<Step5View>("picker");

    const stagedSummary = computed(() => {
        if (store.categoriesSetupMethod === "template") {
            return translate("wizard.step5.staged_template_summary", false, [
                store.categoriesStagedPreviewCount ?? 0,
                store.categoriesStagedSummaryLabel ?? "",
            ]);
        }

        return translate("wizard.step5.staged_copy_summary", false, [
            store.categoriesStagedPreviewCount ?? 0,
            store.categoriesStagedSummaryLabel ?? "",
        ]);
    });

    onMounted(() => {
        if (store.hasPriorEventsForCopy === null) {
            eventsApi
                .getEventsToManageForOrganizer(undefined, true)
                .then(groupedEvents => {
                    store.hasPriorEventsForCopy =
                        groupedEvents.incomingEvents.length + groupedEvents.draftEvents.length + groupedEvents.pastEvents.length > 0;
                })
                .catch(handleError);
        }
    });

    function onTemplateApplied(payload: { templateNames: string[]; categoryPaths: string[]; previewCount: number; summaryLabel: string }) {
        store.categoriesSetupMethod = "template";
        store.categoriesTemplateNames = payload.templateNames;
        store.categoriesCategoryPaths = payload.categoryPaths;
        store.categoriesSourceEventId = null;
        store.categoriesStagedPreviewCount = payload.previewCount;
        store.categoriesStagedSummaryLabel = payload.summaryLabel;
        view.value = "picker";
    }

    function onCopied(payload: { sourceEventId: number; previewCount: number; summaryLabel: string }) {
        store.categoriesSetupMethod = "copyFromPreviousEvent";
        store.categoriesTemplateNames = null;
        store.categoriesCategoryPaths = null;
        store.categoriesSourceEventId = payload.sourceEventId;
        store.categoriesStagedPreviewCount = payload.previewCount;
        store.categoriesStagedSummaryLabel = payload.summaryLabel;
        view.value = "picker";
    }

    function onChange() {
        view.value = store.categoriesSetupMethod === "template" ? "template" : "copy";
    }

    function onBackFromSubView() {
        store.categoriesSetupMethod = null;
        store.categoriesTemplateNames = null;
        store.categoriesCategoryPaths = null;
        store.categoriesSourceEventId = null;
        store.categoriesStagedPreviewCount = null;
        store.categoriesStagedSummaryLabel = null;
        view.value = "picker";
    }

    function onFinish() {
        if (store.categoriesSetupMethod === "template") {
            emit(
                "submit",
                {
                    templateNames: store.categoriesTemplateNames ?? [],
                    categoryPaths: store.categoriesCategoryPaths ?? [],
                },
                "template",
            );
            return;
        }

        if (store.categoriesSetupMethod === "copyFromPreviousEvent") {
            emit(
                "submit",
                {
                    sourceEventId: store.categoriesSourceEventId ?? undefined,
                },
                "copyFromPreviousEvent",
            );
            return;
        }

        emit("submit", null, view.value === "manualExplainer" ? "manual" : "skip");
    }

    function translateField(rawField: string): string {
        const baseField = rawField.replace(/\[\d+\]/g, "");
        return translate(`wizard.field.${baseField}`) ?? baseField;
    }
</script>

<style scoped>
    .step5-option-card {
        border: 1px solid var(--bulma-grey);
    }
</style>
